import type { ReminderAction } from "shared/features/notification";

// apps/desktop 의 preload(src/preload/index.ts)가 노출하는 네이티브 브릿지 타입.
// apps/app 은 apps/desktop 에 의존하지 않으므로(반대 방향 의존만 허용) 형태를 여기
// 손으로 맞춰둔다 — 브릿지 표면이 바뀌면 두 파일을 같이 고친다.
export type DesktopBridge = {
  platform: "desktop";
  getVersion: () => string;
  notify: (title: string, body?: string) => void;
  /** 시스템 기본 브라우저로 url을 연다(우리 앱 origin으로만 제한됨). */
  openExternal: (url: string) => void;
  /** withtask:// 딥링크 수신. 해제 함수를 반환한다. */
  onDeepLink: (callback: (url: string) => void) => () => void;
  /** 네이티브 알림 클릭 시 이동할 url 수신. 해제 함수를 반환한다. */
  onNotificationClick: (callback: (url: string) => void) => () => void;
  /** 콜드 오픈(트레이 상주 중 알림 클릭으로 앱이 열림) 시 리스너 등록 전 도착해 보관된
   *  이동 url 을 한 번 가져간다(없으면 null). main/NotificationManager.ts 참고. */
  consumeNotificationClick: () => Promise<string | null>;
  /** 로그인/로그아웃 시 현재 userId를 main process에 알린다(로그아웃이면 null). */
  setUserId: (userId: string | null) => void;
  /** 로그인/로케일 확정 시 리마인더 알림 버튼(완료/스누즈) 라벨을 현재 로케일 번역으로
   *  main process에 넘긴다(main에는 lingui가 없어 여기서 번역해 전달). */
  setReminderLabels: (labels: { complete: string; snooze: string }) => void;
  /** 테마 배경/전경색이 바뀔 때마다 호출해 Electron 창 배경(및 Windows
   *  titleBarOverlay) 색을 실시간으로 맞춘다. */
  updateThemeColors: (background: string, foreground: string) => void;
  /** 리마인더 알림의 완료/스누즈 버튼 클릭(main process의 NotificationManager가 IPC로
   * 전달) 수신. 해제 함수를 반환한다. */
  onNotificationAction: (
    callback: (payload: { action: ReminderAction; taskId: string }) => void,
  ) => () => void;
};

declare global {
  interface Window {
    desktop?: DesktopBridge;
  }
}

/** Electron 셸 안에서 실행 중이면 네이티브 브릿지를, 웹 브라우저면 undefined 를 반환한다. */
export function getDesktopBridge(): DesktopBridge | undefined {
  return typeof window !== "undefined" ? window.desktop : undefined;
}
